// Generic Engineering Context strip — replaces the per-report bespoke
// banners (DMR's hand-rolled version, the Kill Sheet's contextSnapshot
// chip) with one component the framework reuses on every consumer.
//
// Renders the resolved value for each `consumes[]` path plus a confidence
// indicator. Never throws; an unresolved path simply shows "—".

import React, { useMemo } from "react";
import { resolveMany } from "@/lib/rce/resolver";

/**
 * Props:
 *   - consumerKey:   string  — e.g. "cementingReport" (used for testIds)
 *   - consumerLabel: string  — friendly label shown in the strip header
 *   - consumes:      string[] — RCE field paths to display
 *   - ctx:           object  — resolver context (project/well/rig/... ids)
 *   - dense:         boolean — compact rendering (3 cols vs 2)
 */
export default function RceContextStrip({
  consumerKey,
  consumerLabel,
  consumes = [],
  ctx = {},
  dense = false,
}) {
  const resolved = useMemo(() => resolveMany(consumes, ctx), [consumes, ctx]);

  if (!consumes || consumes.length === 0) return null;

  const tid = (suffix) => `${consumerKey || "rce"}-context-${suffix}`;

  const cols = dense ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2 sm:grid-cols-3";

  return (
    <div
      data-testid={tid("strip")}
      className="card-rs p-3"
      style={{
        background:   "rgba(255,179,0,0.06)",
        borderColor:  "var(--rs-border)",
        borderLeft:   "2px solid var(--rs-amber)",
      }}
    >
      <div
        className="text-[10px] tracking-[0.18em] uppercase font-semibold mb-1"
        style={{ color: "var(--rs-amber)" }}
        data-testid={tid("label")}
      >
        Engineering Context · {consumerLabel || consumerKey || "Consumer"}
      </div>
      <div className={`text-[11px] text-[var(--rs-text-2)] grid ${cols} gap-x-3 gap-y-0.5`}>
        {consumes.map((path) => {
          const r = resolved[path] || { value: "", confidence: "unknown" };
          const known = r.confidence !== "unknown" && r.confidence !== "conflict";
          const display = known && r.value !== "" && r.value != null ? String(r.value) : "—";
          return (
            <span
              key={path}
              data-testid={tid(`field-${path.replace(/\./g, "-")}`)}
              title={path}
            >
              <b className="text-[var(--rs-text-3)]">{_humanise(path)}:</b>{" "}
              <span className={known ? "" : "text-[var(--rs-text-3)]"}>{display}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

// Convert an rcePath into a short human label. Pure — kept inside the
// component because no other surface needs it yet. Keeps the strip readable
// without forcing every binding row to declare a label for every field.
function _humanise(path) {
  const last = String(path).split(".").pop() || path;
  return last
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase())
    .replace(/\s(Ft|Bbl|Ppg|In|Psi|Stk)$/i, (m) => " " + m.trim().toLowerCase())
    .trim();
}
